import { TranslationService } from "../services/TranslationService";
import { ObjetTouristique } from "../types/apidae";
import { WidgetLanguage } from "../types/widgets";

type IllustrationLike = {
  traductionFichiers?: Array<{
    locale?: string;
    url?: string;
    urlListe?: string;
    urlFiche?: string;
    urlDiaporama?: string;
  }>;
};

/**
 * Détecte l'environnement basé sur le hostname de la page
 * et retourne l'URL d'API correspondante
 */
export function getApiUrlFromEnvironment(): string {
  // Détecter l'environnement depuis le hostname
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  // Mapping des environnements vers les URLs d'API
  const environmentApiUrls: Record<string, string> = {
    // Production
    "apidae-tourisme.com": "https://api.apidae-tourisme.com",
    // Preprod
    "apidae-tourisme.dev": "https://api.apidae-tourisme.dev",
    // Cooking
    "apidae-tourisme.cooking": "https://api.apidae-tourisme.cooking",
    // Local
    localhost: "http://api.apidae-tourisme.home",
    "127.0.0.1": "http://api.apidae-tourisme.home",
  };

  // Vérifier si le hostname correspond exactement à un mapping
  if (hostname && environmentApiUrls[hostname]) {
    return environmentApiUrls[hostname];
  }

  // Vérifier si le hostname contient un sous-domaine d'environnement
  for (const [envHostname, apiUrl] of Object.entries(environmentApiUrls)) {
    if (
      hostname.includes(envHostname) ||
      hostname.endsWith(`.${envHostname}`)
    ) {
      return apiUrl;
    }
  }

  // Par défaut, utiliser l'URL de production
  return "https://api.apidae-tourisme.com";
}

/**
 * Récupère l'URL de l'image principale d'un item.
 * Si language est fourni, utilise la traduction correspondante (comme getIllustrationImageUrl).
 */
export function getImageUrl(
  item: ObjetTouristique,
  size: "liste" | "fiche" | "diaporama" | "original" = "liste",
  language?: string,
): string {
  const illustration = item.illustrations?.[0];
  return getIllustrationImageUrl(
    illustration as IllustrationLike | undefined,
    size,
    language,
  );
}

/**
 * Récupère l'URL d'une illustration pour une taille donnée (original, fiche, etc.).
 * Si language est fourni, utilise la traduction dont locale correspond (comme pour les multimédias), sinon la première.
 */
export function getIllustrationImageUrl(
  illustration: IllustrationLike | undefined,
  size: "liste" | "fiche" | "diaporama" | "original" = "original",
  language?: string,
): string {
  const fichiers = illustration?.traductionFichiers;
  if (!fichiers?.length) return "";
  const first = fichiers[0];
  const traduction = language
    ? fichiers.find((f) => f.locale === language) || first
    : first;
  switch (size) {
    case "liste":
      return traduction.urlListe || traduction.url || "";
    case "fiche":
      return traduction.urlFiche || traduction.url || "";
    case "diaporama":
      return traduction.urlDiaporama || traduction.url || "";
    case "original":
    default:
      return traduction.url || "";
  }
}

/**
 * Récupère le libellé traduit d'un objet multilingue
 */
export function getLabel(libelle: any, language: WidgetLanguage): string {
  return TranslationService.getLabelWithFallback(libelle, language);
}

/**
 * Traduit une clé de l'interface
 */
export function t(key: string, language: WidgetLanguage): string {
  return TranslationService.translate(key, language);
}

/**
 * Formate une date selon la langue
 */
export function formatDate(
  dateStr: string,
  language: WidgetLanguage,
  options?: Intl.DateTimeFormatOptions,
): string {
  return TranslationService.formatDate(dateStr, language, options);
}
